import type React from "react";
import styled from "styled-components";
import {
  type StudioActionResult,
  type StudioActionRow,
  useStudioActionsHost,
} from "./StudioActionsContext";
import {
  actionButton,
  actionLabel,
  groupOf,
  parseStudioActions,
} from "./studioActionFence";

// A `json:studio` fence in the chat: one row per action. Auto rows show
// whether the Studio applied them; click rows carry the button that lands
// the action (Insert's contract). Without a host the rows are labels only.

interface Props {
  code: string;
  messageId: string;
}

type RowStatus = "done" | "refused" | "ready" | "off";

function hostFreeRows(code: string): {
  rows: StudioActionRow[];
  errors: string[];
} {
  const { actions, errors } = parseStudioActions(code);
  return {
    rows: actions.map((a, index) => ({
      index,
      group: groupOf(a),
      label: actionLabel(a),
      button: actionButton(a),
      enabled: false,
    })),
    errors,
  };
}

function statusOf(
  row: StudioActionRow,
  result: StudioActionResult | undefined,
): RowStatus {
  if (result) return result.pending ? "ready" : result.ok ? "done" : "refused";
  return row.enabled ? "ready" : "off";
}

function ActionRow({
  row,
  result,
  onRun,
}: {
  row: StudioActionRow;
  result: StudioActionResult | undefined;
  onRun?: () => void;
}) {
  const status = statusOf(row, result);
  const note = result?.text ?? row.reason;
  const showButton = row.group === "click" && !result && onRun;
  return (
    <Row
      data-testid="studio-action-row"
      data-group={row.group}
      data-status={status}
    >
      <Dot $status={status} aria-label={status} />
      <Label>{row.label}</Label>
      {note && <Note>{note}</Note>}
      {showButton && (
        <RunButton type="button" disabled={!row.enabled} onClick={onRun}>
          {row.button}
        </RunButton>
      )}
    </Row>
  );
}

export const StudioActionsBlock: React.FC<Props> = ({ code, messageId }) => {
  const host = useStudioActionsHost();
  const { rows, errors } = host ? host.describe(code) : hostFreeRows(code);
  return (
    <Block data-testid="studio-actions-block">
      {rows.map((row) => (
        <ActionRow
          key={row.index}
          row={row}
          result={host?.resultFor(messageId, code, row.index)}
          onRun={host ? () => host.run(messageId, code, row.index) : undefined}
        />
      ))}
      {errors.map((e) => (
        <Row key={e} data-status="error">
          <Dot $status="refused" aria-label="error" />
          <Label>{e}</Label>
        </Row>
      ))}
    </Block>
  );
};

const Block = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 12px 0;
  padding: 6px 10px;
  max-width: 100%;
  overflow: hidden;
  border: 1px solid ${(p) => p.theme.cards1Border};
  border-radius: 6px;
  background: ${(p) => p.theme.tableTheadCellBackground};
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  min-height: 24px;
  font-size: 12px;
`;

const DOT_COLORS: Record<RowStatus, string> = {
  done: "#2ea043",
  refused: "#d1242f",
  ready: "#8c959f",
  off: "#c8ccd1",
};

const Dot = styled.span<{ $status: RowStatus }>`
  flex: none;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${(p) => DOT_COLORS[p.$status]};
`;

// Labels and notes carry ids (resource:<service>:<name>) that have no
// break opportunity of their own — they wrap anywhere rather than run
// out of the box.
const Label = styled.span`
  min-width: 0;
  overflow-wrap: anywhere;
  color: ${(p) => p.theme.semanticTextBody};
`;

const Note = styled.span`
  min-width: 0;
  overflow-wrap: anywhere;
  color: ${(p) => p.theme.semanticTextLight};
  font-style: italic;
`;

const RunButton = styled.button`
  flex: none;
  margin-left: auto;
  padding: 4px 10px;
  white-space: nowrap;
  background: ${(p) => p.theme.buttonSecondaryHoverBackground};
  border: 1px solid ${(p) => p.theme.buttonSecondaryHoverBorder};
  border-radius: 4px;
  color: ${(p) => p.theme.buttonSecondaryIcon};
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;
